import { useTranslation } from 'react-i18next'
import { formatCalendarDate } from '../../utils/warehouseDates'
import { FranjaCapacityStrip, boardLocale } from './FranjaCapacityStrip'

const T = 'replenishments.inbound_appointments.board'

// 'lun 17' — pure calendar rendering of a YYYY-MM-DD string.
const formatDayHeader = (dateStr, locale) => formatCalendarDate(dateStr, locale, { weekday: 'short', day: 'numeric' })

const headerCellClasses = 'px-2 py-2 border-b border-gray-200 border-l border-l-gray-100 text-center'
const bodyCellClasses = 'px-2 py-2 border-b border-gray-100 border-l border-l-gray-100 text-center align-middle'

// Shared shape for the week grid's chip-count rows (extemporáneas / early
// arrivals): a label cell, then one cell per date with a chip or '—'.
const ChipCountRow = ({ label, labelClassName, counts, dates, cellClassName, chipClassName }) => (
  <tr>
    <td className={labelClassName}>{label}</td>
    {counts.map((count, index) => (
      <td key={dates[index]} className={cellClassName(dates[index])}>
        {count > 0 ? <span className={chipClassName}>{count}</span> : <span className="text-gray-300">—</span>}
      </td>
    ))}
  </tr>
)

// Router-free 7-day compilation grid (spec 2026-08-18 week-overview §3).
// Every day header and cell is a drill-down button: onSelectDate(date) — the
// container flips back to the day board with that date picked.
export const WeekOverviewGrid = ({ overview, todayDate = null, onSelectDate }) => {
  const { i18n } = useTranslation()
  const locale = boardLocale(i18n)
  const todayColumnClasses = date => (date === todayDate ? ' bg-indigo-50' : '')

  // A franja-less warehouse must not render a hollow table (header + zero
  // totals around an empty body) — it reads as a rendering bug (eng-review D6).
  if (overview.franjaRows.length === 0 && !overview.hasExtemporaneous && !overview.hasEarly) {
    return <div className="text-center py-12 text-gray-500">{i18n.t(`${T}.week.empty`)}</div>
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full bg-white border border-gray-200 rounded-md text-sm" style={{ minWidth: '860px' }}>
        <thead>
          <tr>
            <th className="px-3 py-2 border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              {i18n.t(`${T}.week.franja_header`)}
            </th>
            {overview.dates.map(date => {
              // Localized, differentiated accessible names (OV#8/D13): the
              // header announces "Ver lun 17"; each cell below appends its
              // franja so no two buttons in a column share a name.
              const dayLabel = formatDayHeader(date, locale)
              return (
                <th key={date} className={headerCellClasses + todayColumnClasses(date)}>
                  <button
                    type="button"
                    className="w-full text-center hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50"
                    onClick={() => onSelectDate(date)}
                    aria-label={i18n.t(`${T}.week.header_label`, { day: dayLabel })}
                  >
                    <span className={`block font-semibold ${date === todayDate ? 'text-indigo-700' : 'text-gray-700'}`}>{dayLabel}</span>
                    <span className="block text-[11px] text-indigo-700">{date === todayDate ? i18n.t(`${T}.week.today`) : ' '}</span>
                  </button>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {overview.franjaRows.map(row => (
            <tr key={row.franja.id}>
              <td className="px-3 py-2 border-b border-gray-100 whitespace-nowrap">
                <div className="font-medium text-gray-900">{row.franja.label}</div>
                <div className="text-xs text-gray-500">{`${row.franja.starts_at} – ${row.franja.ends_at}`}</div>
              </td>
              {row.cells.map(cell => (
                <td key={cell.date} className={bodyCellClasses + todayColumnClasses(cell.date)}>
                  <button
                    type="button"
                    className="w-full text-center hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50"
                    onClick={() => onSelectDate(cell.date)}
                    aria-label={i18n.t(`${T}.week.cell_label`, { day: formatDayHeader(cell.date, locale), franja: row.franja.label })}
                  >
                    <span className={`block ${cell.arrivals > 0 ? 'font-semibold text-gray-900' : 'text-gray-400'}`}>
                      {cell.arrivals > 0 ? i18n.t(`${T}.arrivals_count`, { count: cell.arrivals }) : '0'}
                    </span>
                    <FranjaCapacityStrip summary={cell.summary} />
                  </button>
                </td>
              ))}
            </tr>
          ))}
          {overview.hasExtemporaneous && (
            <ChipCountRow
              label={i18n.t(`${T}.week.extemporaneous_row`)}
              labelClassName="px-3 py-2 border-b border-gray-100 text-xs font-medium text-amber-800 whitespace-nowrap"
              counts={overview.extemporaneousCounts}
              dates={overview.dates}
              cellClassName={date => bodyCellClasses + todayColumnClasses(date)}
              chipClassName="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800"
            />
          )}
          <tr className="bg-gray-50">
            <td className="px-3 py-2 text-xs font-semibold text-gray-700 border-t border-gray-200">{i18n.t(`${T}.week.total_row`)}</td>
            {overview.totals.map((total, index) => (
              <td
                key={overview.dates[index]}
                className={`px-2 py-2 text-center border-t border-gray-200 border-l border-l-gray-100${todayColumnClasses(overview.dates[index])}`}
              >
                <span className={total > 0 ? 'font-bold text-gray-900' : 'text-gray-400'}>{total}</span>
              </td>
            ))}
          </tr>
          {overview.hasEarly && (
            <ChipCountRow
              label={i18n.t(`${T}.week.early_row`)}
              labelClassName="px-3 py-2 text-xs font-medium text-gray-500 whitespace-nowrap"
              counts={overview.earlyCounts}
              dates={overview.dates}
              cellClassName={date => `px-2 py-2 text-center border-l border-l-gray-100${todayColumnClasses(date)}`}
              chipClassName="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600"
            />
          )}
        </tbody>
      </table>
    </div>
  )
}
